import ai from "../axios/ai";

/**
 * RAG / AI document processing.
 *
 * After a patient document is uploaded (which returns the new document's id),
 * we queue it for AI processing on the ML service (BASE_URL_ML). This is a
 * fire-and-forget side effect: it must never block or fail the upload /
 * onboarding flow it is attached to.
 *
 * Endpoint: POST /rag/process-document/  { document_id, db_schema }
 *   -> { document_id, status: "queued" }
 *
 * Uses the `ai` client (no auth header), matching the other ML endpoints
 * (e.g. /chatbot/process-s3-reports/ and the chatbot summary that take
 * db_schema). The db_schema is the tenant schema slug captured at login.
 */
// The ML endpoint expects an application/x-www-form-urlencoded body (FastAPI
// Form fields), NOT JSON — a JSON body is ignored and the server returns 422
// "field required". We also use the no-trailing-slash path: /rag/process-document/
// 307-redirects to the no-slash route and the body is dropped across the redirect.
export const processDocument = (documentId, dbSchema) => {
  const body = new URLSearchParams();
  body.append("document_id", documentId);
  body.append("db_schema", dbSchema);
  return ai.post("/rag/process-document", body, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
};

/**
 * Pull document ids out of an upload API response. The upload endpoint may
 * return a single document object or an array of them, and the id may be on
 * `id` or `document_id` — handle all of those so callers don't have to.
 */
export const extractDocumentIds = (resData) => {
  const arr = Array.isArray(resData) ? resData : resData ? [resData] : [];
  return arr.map((d) => d?.id ?? d?.document_id).filter((v) => v != null);
};

/**
 * Queue one or more uploaded documents for AI processing. Best-effort:
 * resolves even if some/all calls fail (Promise.allSettled), and no-ops when
 * there is no schema or no ids — so it is always safe to call without await.
 */
export async function processDocumentsForAI(documentIds = []) {
  const dbSchema = sessionStorage.getItem("schema_name");
  const ids = (documentIds || []).filter((v) => v != null);
  if (!dbSchema || ids.length === 0) return;
  await Promise.allSettled(ids.map((id) => processDocument(id, dbSchema)));
}
